import React, { useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, ChevronRight, ChevronLeft, Sun, Moon, AlertTriangle } from 'lucide-react';
import StepIndicator from './StepIndicator';
import { MASTER_TIME_SLOTS } from '../../constants/timeSlots'; // ✅ 引入共用時段定義

const TimeSelectionStyles = () => (
  <style>{`
    .time-section {
      max-width: 900px;
      margin: 0 auto;
      background: #ffffff;
      padding: clamp(20px, 4vw, 40px);
      border-radius: 4px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    }

    .time-section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: clamp(20px, 4vw, 35px);
      padding-bottom: clamp(15px, 3vw, 20px);
      border-bottom: 2px solid #f0f0f0;
      width: 100%;
      flex-wrap: wrap;
      gap: 15px;
    }

    .time-section-title {
      font-size: clamp(1.25rem, 4vw, 1.6rem);
      font-weight: 400;
      color: #1a1a1a;
      display: flex;
      align-items: center;
      gap: clamp(8px, 2vw, 12px);
      letter-spacing: -0.5px;
      flex: 1;
      min-width: 200px;
    }

    .time-section-title svg {
      width: clamp(20px, 4vw, 24px);
      height: clamp(20px, 4vw, 24px);
      flex-shrink: 0;
    }

    .time-helper-text {
      font-size: clamp(0.8rem, 2vw, 0.9rem);
      color: #666;
      background: #f5f5f5;
      padding: clamp(6px, 1.5vw, 8px) clamp(15px, 3vw, 20px);
      border-radius: 20px;
      white-space: nowrap;
    }

    /* 配送卡片樣式 */
    .delivery-card {
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 20px;
      margin-bottom: 25px;
      background: #fff;
    }

    .delivery-card-header {
      display: flex;
      align-items: center;
      gap: 15px;
      margin-bottom: 15px;
      padding-bottom: 15px;
      border-bottom: 1px dashed #e8e8e8;
    }

    .meal-icon-wrapper {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .icon-lunch { background: #fff9db; color: #f59f00; }
    .icon-dinner { background: #e7f5ff; color: #1c7ed6; }

    .delivery-info h3 {
      font-size: 1rem;
      font-weight: 400;
      margin: 0 0 4px 0;
      color: #1a1a1a;
    }

    .delivery-info p {
      margin: 0;
      font-size: 0.85rem;
      color: #888;
    }

    .time-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
      gap: 10px;
    }

    .time-button {
      padding: 12px 10px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: #fff;
      color: #111;
      font-size: 0.9rem;
      font-weight: 400;
    }

    .time-button:hover {
      border-color: #2F7D5E;
    }

    .time-button:active {
      transform: scale(0.98);
    }

    .time-button-selected {
      background: #2F7D5E;
      color: #fff;
      border-color: #2F7D5E;
      box-shadow: 0 4px 12px rgba(47,125,94,0.2);
    }

    .time-btn-container {
      display: flex;
      justify-content: center;
      gap: 15px;
      border-top: 1px solid #f0f0f0;
      padding-top: clamp(20px, 4vw, 30px);
      margin-top: 30px;
      flex-wrap: wrap;
    }

    .time-btn {
      padding: clamp(14px, 3vw, 16px) clamp(30px, 5vw, 40px);
      border: none;
      font-size: 16px;
      font-weight: 400;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      letter-spacing: 0.3px;
      border-radius: 4px;
      min-width: 140px;
      flex: 1;
    }

    .time-btn-primary { background: #2F7D5E; color: #fff; box-shadow: 0 4px 12px rgba(47,125,94,0.2); border: 1px solid #2F7D5E; }
    .time-btn-secondary { background: #fff; color: #2F7D5E; border: 1px solid #e0e0e0; }
    .time-btn-disabled { background: #e8e8e8; color: #aaa; cursor: not-allowed; box-shadow: none; }

    /* RWD & Dark Mode */
    @media (max-width: 768px) {
      .time-section-header { flex-direction: column; align-items: flex-start; }
      .time-helper-text { width: 100%; text-align: center; margin-top: 10px; }
      .time-btn-container { flex-direction: column; }
      .time-btn { width: 100%; }
    }

    @media (prefers-color-scheme: dark) {
      .time-section { background: #1a1a1a; }
      .time-section-title { color: #ffffff; }
      .time-helper-text { background: #2a2a2a; color: #999; }

      .delivery-card { background: #222; border-color: #333; }
      .delivery-card-header { border-bottom-color: #333; }
      .delivery-info h3 { color: #fff; }

      .icon-lunch { background: #332b00; color: #ffd43b; }
      .icon-dinner { background: #002b4d; color: #339af0; }

      .time-button { background: #2a2a2a; border-color: #404040; color: #fff; }
      .time-button:hover { border-color: #2F7D5E; }
      .time-button-selected { background: #2F7D5E; color: #fff; border-color: #2F7D5E; }

      .time-btn-primary { background: #2F7D5E; color: #fff; }
      .time-btn-secondary { background: #2a2a2a; color: #fff; border-color: #404040; }
      .time-btn-disabled { background: #333; color: #666; }
    }
  `}</style>
);

const TimeSelection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('booking');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const {
    next7Days,
    bookingMode,
    mealSelections,
    selectedTime,
    setSelectedTime,
    handleSubmitTime,
    matchedZone
  } = useOutletContext();

  // --- 1. 計算該區域允許的時段 ---
  // 如果沒有 matchedZone (使用者繞過第一步)，則為空陣列
  const allowedSlotIds = matchedZone?.time_slots || [];

  // 過濾出可用的午餐時段 (字串陣列)
  const availableLunchSlots = MASTER_TIME_SLOTS
    .filter(slot => slot.category === 'lunch' && allowedSlotIds.includes(slot.id))
    .map(slot => slot.time);

  // 過濾出可用的晚餐時段 (字串陣列)
  const availableDinnerSlots = MASTER_TIME_SLOTS
    .filter(slot => slot.category === 'dinner' && allowedSlotIds.includes(slot.id))
    .map(slot => slot.time);

  // --- 2. 動態生成需要選擇時間的任務列表 ---
  const deliveryTasks = [];

  if (mealSelections) {
    Object.keys(mealSelections).forEach(dayId => {
      const selections = mealSelections[dayId];
      const day = next7Days.find(d => d.id === parseInt(dayId));

      if (day && selections) {
        if (selections.lunch) {
          deliveryTasks.push({
            id: `${dayId}_lunch`,
            day: day,
            type: 'lunch',
            title: t('time_selection.lunch_delivery'),
            slots: availableLunchSlots // ✅ 使用過濾後的時段
          });
        }
        if (selections.dinner) {
          deliveryTasks.push({
            id: `${dayId}_dinner`,
            day: day,
            type: 'dinner',
            title: t('time_selection.dinner_delivery'),
            slots: availableDinnerSlots // ✅ 使用過濾後的時段
          });
        }
      }
    });
  }

  // 排序
  deliveryTasks.sort((a, b) => {
    if (a.day.id !== b.day.id) return a.day.id - b.day.id;
    return a.type === 'lunch' ? -1 : 1;
  });

  const handleTimeSelect = (taskId, time) => {
    setSelectedTime(prev => ({
      ...prev,
      [taskId]: time
    }));
  };

  const isAllSelected = deliveryTasks.length > 0 && deliveryTasks.every(task => {
    // 如果該時段沒有可選時間 (slots 為空)，則視為已完成 (或是你要擋住不讓下單)
    // 這裡假設如果沒有時段可選，使用者就無法選，因此 isAllSelected 會是 false (如果嚴格檢查)
    // 比較好的做法是：如果 slots 為空，應該顯示錯誤或不讓進入這頁。
    // 這裡為了彈性：如果有 slots，必須選；如果沒 slots，可以跳過 (或視需求調整)
    if (task.slots.length === 0) return true;
    return selectedTime && selectedTime[task.id];

  });

  const handleNext = () => {
    handleSubmitTime();
  };

  const handleBack = () => {
    navigate('../meals');
  };

  // 🆕 阻擋機制：如果沒有 Zone 資訊，顯示錯誤頁面
  if (!matchedZone) {
      return (
        <>
            <TimeSelectionStyles />
            <StepIndicator currentStep={bookingMode === 'single' ? 2 : 3} bookingMode={bookingMode} />
            <div className="time-section">
                <div style={{textAlign:'center', padding:'50px 20px'}}>
                    <AlertTriangle size={48} color="#e67e22" style={{marginBottom:20}}/>
                    <h2>{t('time_selection.no_zone_title')}</h2>
                    <p style={{color:'#666', marginTop:10}}>
                        {t('time_selection.no_zone_desc')}
                    </p>
                    <p style={{color:'#666', marginTop:5}}>
                        {t('time_selection.no_zone_hint')}
                    </p>
                    <button onClick={() => navigate('../')} className="time-btn time-btn-secondary" style={{marginTop:30, width:'auto', display:'inline-flex'}}>
                        <ChevronLeft/> {t('time_selection.back_address')}
                    </button>
                </div>
            </div>
        </>
      )
  }

  return (
    <>
      <TimeSelectionStyles />
      <StepIndicator
        currentStep={bookingMode === 'single' ? 2 : 3}
        bookingMode={bookingMode}
      />

      <div className="time-section">
        <div className="time-section-header">
          <h2 className="time-section-title">
            <Clock />
            {t('time_selection.title')}
          </h2>
          {/* 配送區域暫時隱藏 */}
        </div>

        {deliveryTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {t('time_selection.no_meals')}
          </div>
        ) : (
          <div>
            {deliveryTasks.map(task => {
              const currentVal = selectedTime ? selectedTime[task.id] : null;
              const isLunch = task.type === 'lunch';

              return (
                <div key={task.id} className="delivery-card">
                  <div className="delivery-card-header">
                    <div className={`meal-icon-wrapper ${isLunch ? 'icon-lunch' : 'icon-dinner'}`}>
                      {isLunch ? <Sun size={20} /> : <Moon size={20} />}
                    </div>
                    <div className="delivery-info">
                      <h3>{task.day.fullDate} ({task.day.weekday}) - {task.title}</h3>
                      <p>{t('time_selection.select_time_hint')}</p>
                    </div>
                  </div>

                  <div className="time-grid">
                    {task.slots.length > 0 ? (
                        task.slots.map(time => (
                        <button
                            key={time}
                            onClick={() => handleTimeSelect(task.id, time)}
                            className={`time-button ${currentVal === time ? 'time-button-selected' : ''}`}
                        >
                            {time}
                        </button>
                        ))
                    ) : (
                        <div style={{gridColumn:'1/-1', color:'#ef4444', fontSize:'0.9rem', padding:'10px', textAlign:'center', background:'#fff2f0', borderRadius:'8px'}}>
                            {t('time_selection.no_slots', { zone: matchedZone.name, type: isLunch ? t('meal_selection.lunch') : t('meal_selection.dinner') })}
                        </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="time-btn-container">
          <button
            onClick={handleBack}
            className="time-btn time-btn-secondary"
          >
            <ChevronLeft size={18} />
            {t('time_selection.back')}
          </button>
          <button
            onClick={handleNext}
            disabled={!isAllSelected}
            className={`time-btn time-btn-primary ${!isAllSelected ? 'time-btn-disabled' : ''}`}
          >
            {t('time_selection.next_payment')}
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </>
  );
};

export default TimeSelection;
